import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/access";
import { roleLabel } from "@/lib/roles";
import MilestoneChangeLog from "@/components/MilestoneChangeLog";
import IndicatorsTab from "@/components/IndicatorsTab";
import ProgressUpdateTab from "@/components/ProgressUpdateTab";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Sparkles, Plus, CheckCircle2, Circle, Clock, FileText, Target, User, Filter, ChevronRight, ChevronDown, Trash2, Pencil } from "lucide-react";

const statusColor = { Todo: "bg-slate-100 text-slate-700", InProgress: "bg-amber-100 text-amber-700", Done: "bg-emerald-100 text-emerald-700", Pending: "bg-slate-100 text-slate-700", Completed: "bg-emerald-100 text-emerald-700" };

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", assignee_id: "", due_date: "", priority: "Medium", milestone_id: "" });
  const [mouPreview, setMouPreview] = useState(null); // { file_id, summary, milestones }
  const [savingMou, setSavingMou] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState("");
  const [expandedTemplate, setExpandedTemplate] = useState("");
  const [renamingTemplate, setRenamingTemplate] = useState(""); // template id
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState("");
  const [csrEdit, setCsrEdit] = useState(null); // { funder_name, implementing_agency, ... }
  const [savingCsr, setSavingCsr] = useState(false);
  const [projectEdit, setProjectEdit] = useState(null); // full project detail edit
  const [savingProject, setSavingProject] = useState(false);
  const [summarising, setSummarising] = useState(false);
  const [aiSummary, setAiSummary] = useState(null); // {text, facts, model}
  // PM milestones tab state
  const [mFilterAssignee, setMFilterAssignee] = useState("all");
  const [mFilterStatus, setMFilterStatus] = useState("all");
  const [expandedBranches, setExpandedBranches] = useState({}); // key = milestone id → boolean (undefined => expanded)
  const [editingMilestone, setEditingMilestone] = useState(null); // {...milestoneCopy}
  const [savingMilestone, setSavingMilestone] = useState(false);
  const [subForm, setSubForm] = useState(null); // { parent, level, title, description, assignee_id, due_date, priority }
  const [savingSub, setSavingSub] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [p, m, t, u, tpl] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/milestones?project_id=${id}`),
      api.get(`/tasks?project_id=${id}`),
      api.get(`/users`).catch(() => ({ data: [] })),
      api.get(`/logframe-templates`).catch(() => ({ data: [] })),
    ]);
    setProject(p.data); setMilestones(m.data); setTasks(t.data); setUsers(u.data); setTemplates(tpl.data || []);
    setLogKey((k) => k + 1);
  };
  useEffect(() => { load(); }, [id]);

  const isPM = user?.role === "Admin" || user?.role === "ProjectManager";
  const canMs = hasPerm(user, "milestones.manage.all_projects") || (isPM && !!project);
  const [logKey, setLogKey] = useState(0);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setExtracting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.info("Parsing LogFrame…");
      const fd2 = new FormData(); fd2.append("file_id", up.data.id);
      const r = await api.post(`/projects/${id}/extract-logframe`, fd2);
      // Preview: show review dialog before persisting
      setMouPreview({
        file_id: r.data.file_id,
        goal: r.data.goal || "",
        summary: r.data.summary || "",
        milestones: (r.data.milestones || []).map((m) => ({ ...m, keep: true })),
      });
      toast.success(`Parsed ${r.data.milestones?.length || 0} milestone(s) — review before saving`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/logframe-template.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "logframe-template.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Template download failed");
    }
  };

  const openProjectEdit = () => {
    setProjectEdit({
      name: project.name || "",
      description: project.description || "",
      project_code: project.project_code || "",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
      total_budget: project.total_budget || 0,
      currency: project.currency || "INR",
      project_manager_id: project.project_manager_id || "",
      status: project.status || "Active",
    });
  };

  const saveProject = async () => {
    if (!projectEdit) return;
    if (!(projectEdit.name || "").trim()) { toast.error("Name is required"); return; }
    setSavingProject(true);
    try {
      const payload = { ...projectEdit, total_budget: parseFloat(projectEdit.total_budget) || 0 };
      await api.patch(`/projects/${id}`, payload);
      toast.success("Project details updated");
      setProjectEdit(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update project");
    } finally { setSavingProject(false); }
  };

  const summariseProject = async () => {
    setSummarising(true);
    try {
      const r = await api.post(`/projects/${id}/summarise`);
      setAiSummary({ text: r.data.summary, facts: r.data.facts, model: r.data.model });
      toast.success("Summary ready");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Summarise failed");
    } finally { setSummarising(false); }
  };

  const updateDraftMilestone = (idx, patch) => {
    setMouPreview((p) => ({ ...p, milestones: p.milestones.map((m, i) => i === idx ? { ...m, ...patch } : m) }));
  };

  const removeDraftMilestone = (idx) => {
    setMouPreview((p) => ({ ...p, milestones: p.milestones.filter((_, i) => i !== idx) }));
  };

  const saveMouDrafts = async () => {
    if (!mouPreview) return;
    const kept = mouPreview.milestones.filter((m) => m.keep !== false);
    if (kept.length === 0) {
      toast.error("Keep at least one milestone or cancel");
      return;
    }
    setSavingMou(true);
    try {
      const payload = {
        file_id: mouPreview.file_id,
        goal: mouPreview.goal,
        summary: mouPreview.summary,
        milestones: kept.map(({ keep, ...m }) => m),
      };
      const r = await api.post(`/projects/${id}/milestones/bulk`, payload);
      toast.success(`Saved ${r.data.created} milestone(s)`);
      setMouPreview(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingMou(false);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...taskForm, project_id: id, due_date: taskForm.due_date || null, milestone_id: taskForm.milestone_id || null, assignee_id: taskForm.assignee_id || null };
      await api.post("/tasks", payload);
      toast.success("Task created");
      setTaskOpen(false);
      setTaskForm({ title: "", description: "", assignee_id: "", due_date: "", priority: "Medium", milestone_id: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const updateTask = async (t, changes) => {
    try {
      await api.patch(`/tasks/${t.id}`, changes);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const updateMilestoneStatus = async (m, status) => {
    try { await api.patch(`/milestones/${m.id}`, { status }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const patchMilestone = async (mid, patch) => {
    try { await api.patch(`/milestones/${mid}`, patch); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteMilestone = async (m) => {
    if (!window.confirm(`Delete "${m.title}"? Items with progress or attachments are archived instead. Any child items lose their parent reference.`)) return;
    try { const r = await api.delete(`/milestones/${m.id}`); toast.success(r.data.archived ? "Archived (has progress)" : "Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openSubForm = (parent, level = "task") => setSubForm({
    parent_id: parent?.id || null,
    parent_code: parent?.code || null,
    parent_title: parent?.title || "",
    level,
    title: "",
    description: "",
    assignee_id: parent?.assignee_id || "",
    due_date: "",
    priority: "Medium",
  });

  const saveSubMilestone = async () => {
    if (!subForm || !(subForm.title || "").trim()) { toast.error("Title required"); return; }
    setSavingSub(true);
    try {
      await api.post("/milestones", {
        project_id: id,
        title: subForm.title.trim(),
        description: subForm.description,
        due_date: subForm.due_date || null,
        level: subForm.level,
        parent_code: subForm.parent_code || null,
        assignee_id: subForm.assignee_id || null,
        priority: subForm.priority,
      });
      toast.success(`${subForm.level[0].toUpperCase()}${subForm.level.slice(1)} added under "${subForm.parent_title || "project"}"`);
      setSubForm(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally { setSavingSub(false); }
  };

  const openMilestoneEdit = (m) => setEditingMilestone({ ...m });
  const splitList = (s) => (s || "").split(";").map((x) => x.trim()).filter(Boolean);

  const saveMilestoneEdit = async () => {
    if (!editingMilestone) return;
    setSavingMilestone(true);
    try {
      await api.patch(`/milestones/${editingMilestone.id}`, {
        title: editingMilestone.title,
        description: editingMilestone.description,
        due_date: editingMilestone.due_date || null,
        priority: editingMilestone.priority || "Medium",
        assignee_id: editingMilestone.assignee_id || null,
        status: editingMilestone.status,
        indicators: splitList(editingMilestone.indicatorsText ?? (editingMilestone.indicators || []).join("; ")),
        means_of_verification: splitList(editingMilestone.movText ?? (editingMilestone.means_of_verification || []).join("; ")),
        progress: Number(editingMilestone.progress || 0),
        comment: editingMilestone.comment || "",
      });
      toast.success("Updated");
      setEditingMilestone(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSavingMilestone(false); }
  };

  const saveTemplate = async () => {
    if (!mouPreview) return;
    const name = (templateName || "").trim();
    if (!name) { toast.error("Template name is required"); return; }
    const kept = mouPreview.milestones.filter((m) => m.keep !== false);
    if (kept.length === 0) { toast.error("Keep at least one milestone"); return; }
    setSavingTemplate(true);
    try {
      await api.post("/logframe-templates", {
        name,
        description: "",
        goal: mouPreview.goal || "",
        summary: mouPreview.summary || "",
        milestones: kept.map(({ keep, ...m }) => m),
      });
      toast.success(`Template "${name}" saved`);
      setTemplateName("");
      const tpl = await api.get("/logframe-templates").catch(() => ({ data: [] }));
      setTemplates(tpl.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally { setSavingTemplate(false); }
  };

  const applyTemplate = async (tid) => {
    if (!tid) return;
    setApplyingTemplate(tid);
    try {
      const r = await api.post(`/projects/${id}/apply-logframe-template/${tid}`);
      toast.success(`Applied "${r.data.template_name}" — ${r.data.created} milestone(s) added`);
      setTemplateDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply template");
    } finally { setApplyingTemplate(""); }
  };

  const startRename = (t) => {
    setRenamingTemplate(t.id);
    setRenameValue(t.name || "");
  };

  const commitRename = async (t) => {
    const name = (renameValue || "").trim();
    if (!name) { toast.error("Name cannot be empty"); return; }
    if (name === t.name) { setRenamingTemplate(""); return; }
    setSavingRename(true);
    try {
      await api.patch(`/logframe-templates/${t.id}`, { name });
      toast.success("Template renamed");
      setRenamingTemplate("");
      const tpl = await api.get("/logframe-templates").catch(() => ({ data: [] }));
      setTemplates(tpl.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rename failed");
    } finally { setSavingRename(false); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    setDeletingTemplate(t.id);
    try {
      await api.delete(`/logframe-templates/${t.id}`);
      toast.success(`Deleted "${t.name}"`);
      const tpl = await api.get("/logframe-templates").catch(() => ({ data: [] }));
      setTemplates(tpl.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setDeletingTemplate(""); }
  };

  const openCsrEdit = () => {
    setCsrEdit({
      funder_name: project.funder_name || "",
      implementing_agency: project.implementing_agency || "",
      schedule_vii_item: project.schedule_vii_item || "",
      location: project.location || "",
      local_area: project.local_area || "Yes",
    });
  };

  const saveCsr = async () => {
    if (!csrEdit) return;
    setSavingCsr(true);
    try {
      await api.patch(`/projects/${id}`, csrEdit);
      toast.success("CSR / UC details updated");
      setCsrEdit(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSavingCsr(false); }
  };

  if (!project) return <div className="p-8 text-slate-500">Loading…</div>;
  const teamUsers = users.filter(u => project.team_member_ids?.includes(u.id) || u.id === project.project_manager_id);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Project</div>
        <div className="flex items-start justify-between mt-1 gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold" data-testid="project-title">{project.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              {project.project_code ? (
                <Badge className="bg-slate-900 text-white font-mono text-[11px]" data-testid="project-code-badge">{project.project_code}</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 text-[11px]" data-testid="project-code-missing">No project code · required for Budget V2</Badge>
              )}
              <p className="text-sm text-slate-500 max-w-2xl">{project.description}</p>
            </div>
            {isPM && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={openProjectEdit} data-testid="edit-project-btn">
                  Edit Project Details
                </Button>
                <Button variant="outline" size="sm" onClick={summariseProject} disabled={summarising} data-testid="summarise-project-btn">
                  <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${summarising ? "animate-pulse" : ""}`} /> {summarising ? "Summarising…" : "Summarise (AI)"}
                </Button>
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="mt-2"><Progress value={project.progress ?? 0} className="w-40 h-1.5" /></div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{project.progress ?? 0}% complete</div>
          </div>
        </div>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "logframe"} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="logframe" data-testid="tab-logframe">Logical Framework</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
          {user?.role !== "Trustee" && <TabsTrigger value="indicators" data-testid="tab-indicators">Indicators</TabsTrigger>}
          {user?.role !== "Trustee" && <TabsTrigger value="progress" data-testid="tab-progress">Progress update</TabsTrigger>}
        </TabsList>

        <TabsContent value="logframe">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-lg">Codified LogFrame Import</h3>
                  <p className="text-sm text-slate-500 mt-1">Upload a structured LogFrame in Excel or CSV format. Deterministic parser maps Goal → Outcome → Output → Activity → Task with indicators and means of verification — no AI, no surprises.</p>
                  {(project.logframe_goal || project.logframe_summary || project.mou_summary) && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-md text-sm text-slate-700 border border-slate-200 space-y-2">
                      {project.logframe_goal && (
                        <div>
                          <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Goal</div>
                          <div>{project.logframe_goal}</div>
                        </div>
                      )}
                      {(project.logframe_summary || project.mou_summary) && (
                        <div>
                          <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Summary</div>
                          <div>{project.logframe_summary || project.mou_summary}</div>
                        </div>
                      )}
                    </div>
                  )}
                  {canMs && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <input type="file" ref={fileRef} accept=".xlsx,.csv" onChange={onUpload} className="hidden" data-testid="logframe-file-input" />
                      <Button onClick={() => fileRef.current?.click()} disabled={extracting} className="bg-[#0F172A] hover:bg-slate-800" data-testid="logframe-upload-btn">
                        {extracting ? <><Sparkles className="w-4 h-4 mr-2 animate-pulse" /> Parsing…</> : <><Upload className="w-4 h-4 mr-2" /> Upload LogFrame (.xlsx / .csv)</>}
                      </Button>
                      <Button variant="outline" onClick={downloadTemplate} data-testid="logframe-template-btn">
                        <FileText className="w-4 h-4 mr-2" /> Download Template
                      </Button>
                      <Button variant="outline" onClick={() => setTemplateDialogOpen(true)} disabled={templates.length === 0} data-testid="apply-template-btn">
                        <FileText className="w-4 h-4 mr-2" /> Apply Template {templates.length > 0 ? `(${templates.length})` : ""}
                      </Button>
                    </div>
                  )}
                  {canMs && <p className="text-xs text-slate-500 mt-2">Columns: <code className="text-[11px]">level · code · title · description · activities · indicators · means_of_verification · due_offset_days</code>. Multi-value cells are separated by semicolons.</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CSR / UC (ICAI Annexure A) — inline editor */}
          <Card className="mt-4">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display font-bold text-lg">CSR / UC Details (ICAI Annexure A)</h3>
                  <p className="text-sm text-slate-500 mt-1">These fields drive the ICAI-format Utilisation Certificate (UC) headers and Part II.</p>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div><span className="text-slate-500">Funder / Company:</span> <span className="font-medium">{project.funder_name || "—"}</span></div>
                    <div><span className="text-slate-500">Implementing Agency:</span> <span className="font-medium">{project.implementing_agency || "—"}</span></div>
                    <div className="sm:col-span-2"><span className="text-slate-500">Schedule VII Item:</span> <span className="font-medium">{project.schedule_vii_item || "—"}</span></div>
                    <div><span className="text-slate-500">Location:</span> <span className="font-medium">{project.location || "—"}</span></div>
                    <div><span className="text-slate-500">Local area:</span> <span className="font-medium">{project.local_area || "—"}</span></div>
                  </div>
                </div>
                {isPM && (
                  <Button variant="outline" onClick={openCsrEdit} data-testid="edit-csr-btn">Edit CSR Fields</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="milestones">
          {milestones.length === 0 ? (
            <div className="space-y-4">
              <Card className="border-dashed"><CardContent className="p-10 text-center text-slate-500 text-sm space-y-3">
                <div>No milestones yet. Upload a Logical Framework from the Logical Framework tab, or add the first Outcome here.</div>
                {canMs && <Button size="sm" variant="outline" onClick={() => openSubForm(null, "outcome")} data-testid="add-root-outcome-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Outcome</Button>}
              </CardContent></Card>
              <MilestoneChangeLog projectId={id} refreshKey={logKey} />
            </div>
          ) : (() => {
            const LEVEL_ORDER = { goal: 0, outcome: 1, output: 2, activity: 3, task: 4 };
            const LEVEL_INDENT = { outcome: 0, output: 24, activity: 48, task: 72 };
            const LEVEL_TINT = {
              outcome: "bg-blue-100 text-blue-700 border-blue-300",
              output: "bg-emerald-100 text-emerald-700 border-emerald-300",
              activity: "bg-amber-100 text-amber-700 border-amber-300",
              task: "bg-purple-100 text-purple-700 border-purple-300",
            };
            const CHILD_LEVEL = { outcome: "output", output: "activity", activity: "task", task: "task" };
            // Filter
            const passesFilter = (m) => {
              if (mFilterAssignee !== "all") {
                if (mFilterAssignee === "unassigned" && m.assignee_id) return false;
                if (mFilterAssignee !== "unassigned" && m.assignee_id !== mFilterAssignee) return false;
              }
              if (mFilterStatus !== "all" && (m.status || "Pending") !== mFilterStatus) return false;
              return true;
            };
            // Sort by code (natural order: 1, 1.1, 1.1.1, ...), fallback to level order + title
            const codeToParts = (c) => (c || "").split(".").map((x) => { const n = parseInt(x); return isNaN(n) ? 9999 : n; });
            const compareCodes = (a, b) => {
              const ap = codeToParts(a); const bp = codeToParts(b);
              for (let i = 0; i < Math.max(ap.length, bp.length); i++) {
                const av = ap[i] ?? -1; const bv = bp[i] ?? -1;
                if (av !== bv) return av - bv;
              }
              return 0;
            };
            const sorted = [...milestones].filter((m) => m.level !== "goal").sort((a, b) => {
              const cc = compareCodes(a.code, b.code);
              if (cc !== 0) return cc;
              return (LEVEL_ORDER[a.level] ?? 9) - (LEVEL_ORDER[b.level] ?? 9);
            });
            const visible = sorted.filter(passesFilter);
            const counts = { total: sorted.length, done: sorted.filter((m) => m.status === "Completed").length, inprog: sorted.filter((m) => m.status === "InProgress").length, unassigned: sorted.filter((m) => !m.assignee_id).length };
            return (
              <div className="space-y-4">
                {/* Filter bar */}
                <Card>
                  <CardContent className="p-3 flex flex-wrap gap-3 items-center">
                    <Filter className="w-4 h-4 text-slate-500" />
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Owner</Label>
                      <Select value={mFilterAssignee} onValueChange={setMFilterAssignee}>
                        <SelectTrigger className="h-8 w-40" data-testid="mfilter-assignee"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Status</Label>
                      <Select value={mFilterStatus} onValueChange={setMFilterStatus}>
                        <SelectTrigger className="h-8 w-36" data-testid="mfilter-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="InProgress">In Progress</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="ml-auto text-xs text-slate-500 flex flex-wrap gap-3">
                      <span data-testid="mcounts-total">Total <b>{counts.total}</b></span>
                      <span className="text-emerald-700">Done <b>{counts.done}</b></span>
                      <span className="text-amber-700">In Progress <b>{counts.inprog}</b></span>
                      <span className="text-red-600">Unassigned <b>{counts.unassigned}</b></span>
                    </div>
                    {canMs && (
                      <Button size="sm" variant="outline" onClick={() => openSubForm(null, "outcome")} data-testid="add-root-outcome-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add Outcome
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Hierarchical list */}
                <div className="space-y-1.5">
                  {visible.map((m) => {
                    const indent = LEVEL_INDENT[m.level] ?? 0;
                    const tint = LEVEL_TINT[m.level] || "bg-slate-100 text-slate-700 border-slate-300";
                    const collapsed = expandedBranches[m.id] === false;
                    const assignee = users.find((u) => u.id === m.assignee_id);
                    const overdue = m.due_date && m.status !== "Completed" && new Date(m.due_date) < new Date();
                    const nextLevel = CHILD_LEVEL[m.level];
                    return (
                      <Card key={m.id} data-testid={`ms-row-${m.id}`} className={`${overdue ? "border-red-200 bg-red-50/30" : ""} hover:shadow-sm transition-shadow`}>
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2" style={{ paddingLeft: `${indent}px` }}>
                            <button
                              type="button"
                              onClick={() => setExpandedBranches({ ...expandedBranches, [m.id]: collapsed })}
                              className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700 shrink-0"
                              data-testid={`ms-toggle-${m.id}`}
                              title={collapsed ? "Expand" : "Collapse"}
                            >
                              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${tint} shrink-0`}>{m.level || "—"}</span>
                            {m.code && <span className="text-[10px] font-mono text-slate-400 shrink-0">{m.code}</span>}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm truncate">{m.title}</span>
                                {overdue && <Badge variant="destructive" className="text-[10px]">Overdue</Badge>}
                              </div>
                              {!collapsed && m.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{m.description}</div>}
                              {!collapsed && m.last_updated_by_name && <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`ms-lastupd-${m.id}`}>Last updated by {m.last_updated_by_name}, {roleLabel(m.last_updated_by_role)} · {(m.last_updated_at || "").slice(0, 16).replace("T", " ")}{m.progress ? ` · ${m.progress}%` : ""}</div>}
                              {!collapsed && (m.indicators?.length > 0 || m.means_of_verification?.length > 0) && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {m.indicators?.map((a, i) => <span key={`i${i}`} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">📊 {a}</span>)}
                                  {m.means_of_verification?.map((a, i) => <span key={`v${i}`} className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">✓ {a}</span>)}
                                </div>
                              )}
                            </div>
                            {/* Assignee inline */}
                            {canMs ? (
                              <Select value={m.assignee_id || "__none"} onValueChange={(v) => patchMilestone(m.id, { assignee_id: v === "__none" ? null : v })}>
                                <SelectTrigger className="h-7 w-36 text-xs" data-testid={`ms-assignee-${m.id}`}>
                                  <SelectValue placeholder="Unassigned">{assignee?.name || <span className="text-slate-400">Unassigned</span>}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none">Unassigned</SelectItem>
                                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="text-xs text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> {assignee?.name || "—"}</div>
                            )}
                            {m.due_date && <span className="text-[10px] font-mono text-slate-500 shrink-0">{m.due_date}</span>}
                            {/* Status */}
                            {canMs ? (
                              <Select value={m.status || "Pending"} onValueChange={(v) => patchMilestone(m.id, { status: v })}>
                                <SelectTrigger className="h-7 w-32 text-xs" data-testid={`ms-status-${m.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Pending">Pending</SelectItem>
                                  <SelectItem value="InProgress">In Progress</SelectItem>
                                  <SelectItem value="Completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className={`status-chip text-[10px] ${statusColor[m.status] || "bg-slate-100"}`}>{m.status || "Pending"}</span>
                            )}
                            {canMs && (
                              <div className="flex items-center gap-0.5 shrink-0">
                                {nextLevel && (
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openSubForm(m, nextLevel)} data-testid={`ms-addchild-${m.id}`} title={`Add ${nextLevel}`}>
                                    <Plus className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openMilestoneEdit(m)} data-testid={`ms-edit-${m.id}`}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteMilestone(m)} data-testid={`ms-delete-${m.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {visible.length === 0 && <Card className="border-dashed"><CardContent className="p-6 text-center text-sm text-slate-500">No milestones match the current filter.</CardContent></Card>}
                </div>
                <MilestoneChangeLog projectId={id} refreshKey={logKey} />
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="tasks">
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm text-slate-500">{tasks.length} tasks</div>
            {canMs && (
              <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
                <DialogTrigger asChild><Button size="sm" className="bg-[#0F172A]" data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1" /> Task</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Assign Task</DialogTitle></DialogHeader>
                  <form onSubmit={createTask} className="space-y-3">
                    <div><Label>Title</Label><Input required value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="task-title-input" /></div>
                    <div><Label>Description</Label><Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Assignee</Label>
                        <Select value={taskForm.assignee_id} onValueChange={(v) => setTaskForm({ ...taskForm, assignee_id: v })}>
                          <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{teamUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Milestone</Label>
                        <Select value={taskForm.milestone_id} onValueChange={(v) => setTaskForm({ ...taskForm, milestone_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                          <SelectContent>{milestones.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Due date</Label><Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} /></div>
                      <div>
                        <Label>Priority</Label>
                        <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Low">Low</SelectItem>
                            <SelectItem value="Medium">Medium</SelectItem>
                            <SelectItem value="High">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter><Button type="submit" data-testid="save-task-btn">Create</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {tasks.length === 0 ? (
            <Card className="border-dashed"><CardContent className="p-10 text-center text-slate-500 text-sm">No tasks yet.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {tasks.map((t) => {
                const assignee = users.find(u => u.id === t.assignee_id);
                const canEdit = canMs || t.assignee_id === user.id;
                return (
                  <Card key={t.id} data-testid={`task-${t.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-sm">{t.title}</h4>
                            <span className={`status-chip ${statusColor[t.status] || "bg-slate-100 text-slate-700"}`}>{t.status}</span>
                            <Badge variant="outline" className="text-[10px]">{t.priority}</Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            {assignee && <span>👤 {assignee.name}</span>}
                            {t.due_date && <span className="font-mono">Due: {t.due_date}</span>}
                          </div>
                        </div>
                        <div className="w-32">
                          <Progress value={t.progress || 0} className="h-1.5" />
                          <div className="text-[10px] font-mono text-slate-500 mt-1 text-right">{t.progress || 0}%</div>
                        </div>
                        {canEdit && (
                          <Select value={t.status} onValueChange={(v) => updateTask(t, { status: v, progress: v === "Done" ? 100 : v === "InProgress" ? Math.max(t.progress || 0, 10) : 0 })}>
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Todo">Todo</SelectItem>
                              <SelectItem value="InProgress">In Progress</SelectItem>
                              <SelectItem value="Done">Done</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="team">
          <ProjectTeamTab projectId={id} currentUser={user} allUsers={users} />
        </TabsContent>
        <TabsContent value="indicators">
          <IndicatorsTab projectId={id} milestones={milestones} users={users} project={project} />
        </TabsContent>
        <TabsContent value="progress">
          <ProgressUpdateTab projectId={id} />
        </TabsContent>
      </Tabs>

      {/* Logical Framework Preview Dialog */}
      <Dialog open={!!mouPreview} onOpenChange={(o) => { if (!o) setMouPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="logframe-preview-dialog">
          <DialogHeader>
            <DialogTitle>Review AI-extracted Logical Framework</DialogTitle>
          </DialogHeader>
          {mouPreview && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase text-slate-500">Goal</Label>
                <Textarea
                  value={mouPreview.goal || ""}
                  onChange={(e) => setMouPreview({ ...mouPreview, goal: e.target.value })}
                  className="mt-1 text-sm"
                  rows={2}
                  placeholder="Overall project goal / impact statement"
                  data-testid="logframe-goal-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500">Summary</Label>
                <Textarea
                  value={mouPreview.summary}
                  onChange={(e) => setMouPreview({ ...mouPreview, summary: e.target.value })}
                  className="mt-1 text-sm"
                  rows={3}
                  data-testid="logframe-summary-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500">Milestones ({mouPreview.milestones.filter((m) => m.keep !== false).length} to save)</Label>
                <div className="space-y-3 mt-2">
                  {mouPreview.milestones.map((m, idx) => (
                    <Card key={idx} className={m.keep === false ? "opacity-40" : ""} data-testid={`logframe-milestone-${idx}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <Input
                            value={m.title}
                            onChange={(e) => updateDraftMilestone(idx, { title: e.target.value })}
                            className="font-semibold"
                            placeholder="Milestone title"
                            data-testid={`logframe-title-${idx}`}
                          />
                          {m.level && (
                            <span className="text-[10px] font-mono uppercase bg-blue-100 text-blue-700 rounded px-2 py-1 self-center shrink-0">{m.level}</span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateDraftMilestone(idx, { keep: m.keep === false })}
                            data-testid={`logframe-toggle-${idx}`}
                          >
                            {m.keep === false ? "Restore" : "Remove"}
                          </Button>
                        </div>
                        <Textarea
                          value={m.description || ""}
                          onChange={(e) => updateDraftMilestone(idx, { description: e.target.value })}
                          placeholder="Description"
                          rows={2}
                          className="text-sm"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Due offset (days from start)</Label>
                            <Input
                              type="number"
                              value={m.due_offset_days ?? ""}
                              onChange={(e) => updateDraftMilestone(idx, { due_offset_days: e.target.value ? parseInt(e.target.value) : null })}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Due date preview</Label>
                            <Input
                              type="date"
                              value={m.due_date || ""}
                              onChange={(e) => updateDraftMilestone(idx, { due_date: e.target.value })}
                            />
                          </div>
                        </div>
                        {m.activities?.length > 0 && (
                          <div>
                            <Label className="text-xs">Activities</Label>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {m.activities.map((a, i) => (
                                <span key={i} className="text-xs bg-slate-100 rounded px-2 py-1 text-slate-700">{a}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {m.indicators?.length > 0 && (
                          <div>
                            <Label className="text-xs">Indicators</Label>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {m.indicators.map((a, i) => (
                                <span key={i} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-2 py-1">{a}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {m.means_of_verification?.length > 0 && (
                          <div>
                            <Label className="text-xs">Means of Verification</Label>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {m.means_of_verification.map((a, i) => (
                                <span key={i} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-1">{a}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between flex-col sm:flex-row items-stretch sm:items-center">
            <div className="flex gap-2 items-center">
              <Input
                placeholder="Save as template name…"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-56"
                data-testid="template-name-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={saveTemplate}
                disabled={savingTemplate || !templateName.trim()}
                data-testid="save-as-template-btn"
              >
                {savingTemplate ? "Saving…" : "Save as Template"}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setMouPreview(null)} data-testid="logframe-cancel-btn">Cancel</Button>
              <Button onClick={saveMouDrafts} disabled={savingMou} className="bg-[#0F172A] hover:bg-slate-800" data-testid="logframe-save-btn">
                {savingMou ? "Saving..." : "Save milestones"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply LogFrame Template dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={(o) => { setTemplateDialogOpen(o); if (!o) setExpandedTemplate(""); }}>
        <DialogContent className="max-w-2xl" data-testid="apply-template-dialog">
          <DialogHeader>
            <DialogTitle>Apply a saved LogFrame template</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Click a template name to preview its milestones before applying.</p>
          </DialogHeader>
          {templates.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">No templates yet. Save one from the AI extraction preview to reuse it.</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {templates.map((t) => {
                const isOpen = expandedTemplate === t.id;
                const mls = t.milestones || [];
                const levelTint = { outcome: "bg-blue-100 text-blue-700", output: "bg-emerald-100 text-emerald-700", activity: "bg-amber-100 text-amber-700" };
                return (
                  <Card key={t.id} data-testid={`template-${t.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => setExpandedTemplate(isOpen ? "" : t.id)}
                          className="flex-1 min-w-0 text-left hover:opacity-80"
                          data-testid={`preview-template-${t.id}-btn`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-xs transition-transform inline-block ${isOpen ? "rotate-90" : ""}`}>▶</span>
                            {renamingTemplate === t.id ? (
                              <Input
                                autoFocus
                                value={renameValue}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") commitRename(t); if (e.key === "Escape") setRenamingTemplate(""); }}
                                className="h-7 text-sm w-64"
                                data-testid={`rename-template-${t.id}-input`}
                              />
                            ) : (
                              <span className="font-semibold">{t.name}</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1 ml-5">{mls.length} milestone(s) · Saved by {t.created_by_name || "—"}</div>
                          {t.goal && <div className="text-xs text-slate-600 mt-1 ml-5 line-clamp-2">Goal: {t.goal}</div>}
                        </button>
                        <div className="flex flex-col gap-1 items-stretch">
                          <Button size="sm" className="bg-[#0F172A]" disabled={applyingTemplate === t.id} onClick={() => applyTemplate(t.id)} data-testid={`apply-template-${t.id}-btn`}>
                            {applyingTemplate === t.id ? "Applying…" : "Apply"}
                          </Button>
                          {renamingTemplate === t.id ? (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs flex-1" disabled={savingRename} onClick={() => commitRename(t)} data-testid={`rename-save-${t.id}-btn`}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs flex-1" onClick={() => setRenamingTemplate("")}>Cancel</Button>
                            </div>
                          ) : (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs flex-1" onClick={() => startRename(t)} data-testid={`rename-template-${t.id}-btn`}>Rename</Button>
                              {user?.role === "Admin" && (
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs flex-1 text-red-600 hover:text-red-700 hover:bg-red-50" disabled={deletingTemplate === t.id} onClick={() => deleteTemplate(t)} data-testid={`delete-template-${t.id}-btn`}>
                                  {deletingTemplate === t.id ? "…" : "Delete"}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-4 ml-5 pl-4 border-l-2 border-slate-200 space-y-3" data-testid={`template-preview-${t.id}`}>
                          {t.summary && (
                            <div className="text-xs text-slate-600"><span className="font-semibold text-slate-700">Summary:</span> {t.summary}</div>
                          )}
                          {mls.length === 0 && <div className="text-xs text-slate-500">No milestones in this template.</div>}
                          {mls.map((m, i) => (
                            <div key={i} className="text-sm">
                              <div className="flex items-center gap-2 flex-wrap">
                                {m.level && <span className={`text-[10px] font-mono uppercase rounded px-1.5 py-0.5 ${levelTint[m.level] || "bg-slate-100 text-slate-700"}`}>{m.level}</span>}
                                <span className="font-medium">{m.title}</span>
                                {m.due_offset_days != null && <span className="text-[10px] text-slate-500 font-mono">+{m.due_offset_days}d</span>}
                              </div>
                              {m.description && <div className="text-xs text-slate-600 mt-0.5">{m.description}</div>}
                              {(m.activities?.length > 0 || m.indicators?.length > 0 || m.means_of_verification?.length > 0) && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {m.activities?.map((a, j) => <span key={`a${j}`} className="text-[10px] bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">{a}</span>)}
                                  {m.indicators?.map((a, j) => <span key={`i${j}`} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">📊 {a}</span>)}
                                  {m.means_of_verification?.map((a, j) => <span key={`v${j}`} className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">✓ {a}</span>)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Milestone dialog */}
      <Dialog open={!!editingMilestone} onOpenChange={(o) => { if (!o) setEditingMilestone(null); }}>
        <DialogContent className="max-w-lg" data-testid="milestone-edit-dialog">
          <DialogHeader><DialogTitle>Edit {editingMilestone?.level || "milestone"}</DialogTitle></DialogHeader>
          {editingMilestone && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={editingMilestone.title || ""} onChange={(e) => setEditingMilestone({ ...editingMilestone, title: e.target.value })} data-testid="ms-edit-title" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={editingMilestone.description || ""} onChange={(e) => setEditingMilestone({ ...editingMilestone, description: e.target.value })} data-testid="ms-edit-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Due Date</Label><Input type="date" value={editingMilestone.due_date || ""} onChange={(e) => setEditingMilestone({ ...editingMilestone, due_date: e.target.value })} data-testid="ms-edit-due" /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={editingMilestone.priority || "Medium"} onValueChange={(v) => setEditingMilestone({ ...editingMilestone, priority: v })}>
                    <SelectTrigger data-testid="ms-edit-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Owner</Label>
                  <Select value={editingMilestone.assignee_id || "__none"} onValueChange={(v) => setEditingMilestone({ ...editingMilestone, assignee_id: v === "__none" ? null : v })}>
                    <SelectTrigger data-testid="ms-edit-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Unassigned</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editingMilestone.status || "Pending"} onValueChange={(v) => setEditingMilestone({ ...editingMilestone, status: v })}>
                    <SelectTrigger data-testid="ms-edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="InProgress">In Progress</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Progress %</Label><Input type="number" min="0" max="100" value={editingMilestone.progress ?? 0} onChange={(e) => setEditingMilestone({ ...editingMilestone, progress: e.target.value })} data-testid="ms-edit-progress" /></div>
              </div>
              <div><Label>Indicators (separate with ;)</Label><Textarea rows={2} value={editingMilestone.indicatorsText ?? (editingMilestone.indicators || []).join("; ")} onChange={(e) => setEditingMilestone({ ...editingMilestone, indicatorsText: e.target.value })} data-testid="ms-edit-indicators" /></div>
              <div><Label>Means of verification (separate with ;)</Label><Textarea rows={2} value={editingMilestone.movText ?? (editingMilestone.means_of_verification || []).join("; ")} onChange={(e) => setEditingMilestone({ ...editingMilestone, movText: e.target.value })} data-testid="ms-edit-mov" /></div>
              <div><Label>Comment</Label><Textarea rows={2} value={editingMilestone.comment || ""} onChange={(e) => setEditingMilestone({ ...editingMilestone, comment: e.target.value })} data-testid="ms-edit-comment" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingMilestone(null)}>Cancel</Button>
            <Button onClick={saveMilestoneEdit} disabled={savingMilestone} className="bg-[#0F172A]" data-testid="ms-edit-save">
              {savingMilestone ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sub-Milestone dialog */}
      <Dialog open={!!subForm} onOpenChange={(o) => { if (!o) setSubForm(null); }}>
        <DialogContent className="max-w-lg" data-testid="sub-milestone-dialog">
          <DialogHeader>
            <DialogTitle>Add {subForm?.level ? subForm.level.charAt(0).toUpperCase() + subForm.level.slice(1) : "item"}</DialogTitle>
            {subForm?.parent_title && <p className="text-xs text-slate-500">Under: {subForm.parent_title}</p>}
          </DialogHeader>
          {subForm && (
            <div className="space-y-3">
              <div><Label>Title *</Label><Input autoFocus value={subForm.title} onChange={(e) => setSubForm({ ...subForm, title: e.target.value })} data-testid="sub-title" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={subForm.description} onChange={(e) => setSubForm({ ...subForm, description: e.target.value })} data-testid="sub-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Due Date</Label><Input type="date" value={subForm.due_date} onChange={(e) => setSubForm({ ...subForm, due_date: e.target.value })} data-testid="sub-due" /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={subForm.priority} onValueChange={(v) => setSubForm({ ...subForm, priority: v })}>
                    <SelectTrigger data-testid="sub-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Owner</Label>
                <Select value={subForm.assignee_id || "__none"} onValueChange={(v) => setSubForm({ ...subForm, assignee_id: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="sub-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Unassigned</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubForm(null)}>Cancel</Button>
            <Button onClick={saveSubMilestone} disabled={savingSub} className="bg-[#0F172A]" data-testid="sub-save">
              {savingSub ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Details dialog */}
      <Dialog open={!!aiSummary} onOpenChange={(o) => { if (!o) setAiSummary(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="ai-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-600" /> AI Project Summary
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Powered by {aiSummary?.model || "Claude Sonnet 5"} · read-only, not stored</p>
          </DialogHeader>
          {aiSummary && (
            <div className="space-y-3">
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800" data-testid="ai-summary-text">
                {aiSummary.text}
              </div>
              <div className="border-t pt-3">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">Underlying figures</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  <div><span className="text-slate-500">Milestones:</span> <b>{aiSummary.facts?.milestones_count}</b></div>
                  {aiSummary.facts?.total_received !== undefined && <div><span className="text-slate-500">Utilisation:</span> <b>{aiSummary.facts?.utilisation_pct}%</b></div>}
                  {aiSummary.facts?.total_received !== undefined && <div><span className="text-slate-500">Received:</span> <b>{aiSummary.facts?.total_received?.toLocaleString()}</b></div>}
                  <div><span className="text-slate-500">Paid out:</span> <b>{aiSummary.facts?.total_paid_out?.toLocaleString()}</b></div>
                  <div><span className="text-slate-500">Advance o/s:</span> <b>{aiSummary.facts?.advance_outstanding?.toLocaleString()}</b></div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAiSummary(null)} data-testid="ai-summary-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Details dialog (below) */}
      <Dialog open={!!projectEdit} onOpenChange={(o) => { if (!o) setProjectEdit(null); }}>
        <DialogContent className="max-w-lg" data-testid="project-edit-dialog">
          <DialogHeader><DialogTitle>Edit Project Details</DialogTitle></DialogHeader>
          {projectEdit && (
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={projectEdit.name} onChange={(e) => setProjectEdit({ ...projectEdit, name: e.target.value })} data-testid="edit-project-name" /></div>
              <div>
                <Label>Project Code <span className="text-xs text-slate-500 font-normal">(required for Budget V2 · uppercase letters/digits · e.g. MUSK, PPRG)</span></Label>
                <Input
                  value={projectEdit.project_code}
                  onChange={(e) => setProjectEdit({ ...projectEdit, project_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                  placeholder="e.g. MUSK"
                  maxLength={12}
                  data-testid="edit-project-code"
                />
              </div>
              <div><Label>Description</Label><Textarea rows={3} value={projectEdit.description} onChange={(e) => setProjectEdit({ ...projectEdit, description: e.target.value })} data-testid="edit-project-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start Date</Label><Input type="date" value={projectEdit.start_date || ""} onChange={(e) => setProjectEdit({ ...projectEdit, start_date: e.target.value })} data-testid="edit-project-start" /></div>
                <div><Label>End Date</Label><Input type="date" value={projectEdit.end_date || ""} onChange={(e) => setProjectEdit({ ...projectEdit, end_date: e.target.value })} data-testid="edit-project-end" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Total Budget</Label><Input type="number" value={projectEdit.total_budget} onChange={(e) => setProjectEdit({ ...projectEdit, total_budget: e.target.value })} data-testid="edit-project-budget" /></div>
                <div>
                  <Label>Currency</Label>
                  <Select value={projectEdit.currency} onValueChange={(v) => setProjectEdit({ ...projectEdit, currency: v })}>
                    <SelectTrigger data-testid="edit-project-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Project Manager</Label>
                <Select value={projectEdit.project_manager_id || ""} onValueChange={(v) => setProjectEdit({ ...projectEdit, project_manager_id: v })}>
                  <SelectTrigger data-testid="edit-project-pm"><SelectValue placeholder="Select PM" /></SelectTrigger>
                  <SelectContent>
                    {users.filter((u) => ["Admin", "ProjectManager"].includes(u.role)).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={projectEdit.status} onValueChange={(v) => setProjectEdit({ ...projectEdit, status: v })}>
                  <SelectTrigger data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Paused">Paused</SelectItem>
                    <SelectItem value="Archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProjectEdit(null)}>Cancel</Button>
            <Button onClick={saveProject} disabled={savingProject} className="bg-[#0F172A] hover:bg-slate-800" data-testid="save-project-btn">
              {savingProject ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit CSR / UC fields dialog */}
      <Dialog open={!!csrEdit} onOpenChange={(o) => { if (!o) setCsrEdit(null); }}>
        <DialogContent data-testid="csr-edit-dialog">
          <DialogHeader><DialogTitle>Edit CSR / UC Details</DialogTitle></DialogHeader>
          {csrEdit && (
            <div className="space-y-3">
              <div><Label>Funder / Company Name</Label><Input value={csrEdit.funder_name} onChange={(e) => setCsrEdit({ ...csrEdit, funder_name: e.target.value })} data-testid="csr-funder-input" /></div>
              <div><Label>Implementing Agency</Label><Input value={csrEdit.implementing_agency} onChange={(e) => setCsrEdit({ ...csrEdit, implementing_agency: e.target.value })} data-testid="csr-agency-input" /></div>
              <div><Label>Schedule VII Item</Label><Input value={csrEdit.schedule_vii_item} onChange={(e) => setCsrEdit({ ...csrEdit, schedule_vii_item: e.target.value })} data-testid="csr-schedule7-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Location</Label><Input value={csrEdit.location} onChange={(e) => setCsrEdit({ ...csrEdit, location: e.target.value })} data-testid="csr-location-input" /></div>
                <div>
                  <Label>Local Area?</Label>
                  <Select value={csrEdit.local_area} onValueChange={(v) => setCsrEdit({ ...csrEdit, local_area: v })}>
                    <SelectTrigger data-testid="csr-localarea-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCsrEdit(null)}>Cancel</Button>
            <Button onClick={saveCsr} disabled={savingCsr} className="bg-[#0F172A] hover:bg-slate-800" data-testid="save-csr-btn">
              {savingCsr ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// =====================================================================
// Project Team tab — add / edit / remove with full history + safeguards.
// Admin + PM-of-this-project may edit; everyone else sees a read-only view.
// =====================================================================
function ProjectTeamTab({ projectId, currentUser, allUsers }) {
  const [data, setData] = useState({ can_edit: false, role_options: [], memberships: [] });
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [showPast, setShowPast] = useState(false);

  const load = () => {
    api.get(`/projects/${projectId}/memberships`).then(r => setData(r.data)).catch(() => {});
  };
  useEffect(load, [projectId]);

  const active = data.memberships.filter(m => m.status === "active");
  const past = data.memberships.filter(m => m.status === "ended");
  const activeUserIds = new Set(active.map(m => m.user_id));
  const addables = (allUsers || []).filter(u => u.is_active !== false && !activeUserIds.has(u.id));

  const isAdmin = currentUser?.role === "Admin";
  const canEdit = !!data.can_edit;

  const canRemove = (m) => {
    if (!canEdit) return false;
    if (isAdmin) return true;
    if (m.user_id === currentUser?.id) return false;
    if (m.role_on_project === "Project Manager") return false;
    return true;
  };
  const canEditRow = (m) => {
    if (!canEdit) return false;
    if (isAdmin) return true;
    if (m.user_id === currentUser?.id) return false;
    return true;
  };

  return (
    <div className="space-y-4" data-testid="team-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">{active.length} active · {past.length} past</div>
        {canEdit && (
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="team-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add member
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr className="border-b">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Role on project</th>
                <th className="text-left px-3 py-2">Start</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-right px-3 py-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {active.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No active members yet.</td></tr>}
              {active.map(m => (
                <tr key={m.id} className="border-b hover:bg-slate-50" data-testid={`team-row-${m.user_id}`}>
                  <td className="px-3 py-2 font-medium">{m.name}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-[11px]">{m.role_on_project}</Badge></td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-500">{m.start_date}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{m.email || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {canEditRow(m) && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(m)} data-testid={`team-edit-${m.user_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    )}
                    {canRemove(m) && (
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setRemoving(m)} data-testid={`team-remove-${m.user_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {past.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <button className="flex items-center gap-1 text-xs font-semibold text-slate-700" onClick={() => setShowPast(!showPast)} data-testid="team-past-toggle">
              {showPast ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Past members ({past.length})
            </button>
            {showPast && (
              <table className="w-full text-xs mt-2">
                <thead className="text-slate-500 uppercase"><tr className="border-b"><th className="text-left px-2 py-1">Name</th><th className="text-left px-2 py-1">Role</th><th className="text-left px-2 py-1">Start</th><th className="text-left px-2 py-1">End</th><th className="text-left px-2 py-1">Reason</th></tr></thead>
                <tbody>
                  {past.map(m => (
                    <tr key={m.id} className="border-b border-slate-100" data-testid={`team-past-${m.user_id}`}>
                      <td className="px-2 py-1">{m.name}</td>
                      <td className="px-2 py-1">{m.role_on_project}</td>
                      <td className="px-2 py-1 font-mono">{m.start_date}</td>
                      <td className="px-2 py-1 font-mono">{m.end_date}</td>
                      <td className="px-2 py-1 text-slate-500">{m.reason}{m.reason_note ? ` — ${m.reason_note}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      <TeamAddDialog open={addOpen} onOpenChange={setAddOpen} addables={addables} roleOptions={data.role_options} projectId={projectId} onSaved={() => { setAddOpen(false); load(); }} />
      <TeamEditDialog membership={editing} onClose={() => setEditing(null)} roleOptions={data.role_options} projectId={projectId} onSaved={() => { setEditing(null); load(); }} />
      <TeamRemoveDialog membership={removing} onClose={() => setRemoving(null)} projectId={projectId} onSaved={() => { setRemoving(null); load(); }} />
    </div>
  );
}

function TeamAddDialog({ open, onOpenChange, addables, roleOptions, projectId, onSaved }) {
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("Project Associate");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setUserId(""); setRole("Project Associate"); setStartDate(new Date().toISOString().slice(0, 10)); setQuery(""); } }, [open]);
  const filtered = addables.filter(u => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.employee_code || "").toLowerCase().includes(q);
  });
  const save = async () => {
    if (!userId) return toast.error("Pick a person");
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/memberships`, { user_id: userId, role_on_project: role, start_date: startDate });
      toast.success("Member added");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="team-add-dialog">
        <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Search by name, email or employee code</Label>
            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Start typing…" data-testid="team-add-search" />
            <div className="mt-2 border rounded max-h-56 overflow-auto">
              {filtered.length === 0 && <div className="p-3 text-xs text-slate-500">No active users match. If the person isn't registered yet, add them in <b>Users &amp; Roles</b> first (Super Admin / Admin only).</div>}
              {filtered.map(u => (
                <label key={u.id} className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${userId === u.id ? "bg-emerald-50" : ""}`} data-testid={`team-add-opt-${u.id}`}>
                  <input type="radio" name="pick" checked={userId === u.id} onChange={() => setUserId(u.id)} />
                  <span className="flex-1">{u.name} <span className="text-xs text-slate-500">· {u.email}</span></span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Role on project</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="team-add-role"><SelectValue /></SelectTrigger>
                <SelectContent>{(roleOptions || []).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start date</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="team-add-start" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!userId || saving} data-testid="team-add-save">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamEditDialog({ membership, onClose, roleOptions, projectId, onSaved }) {
  const [role, setRole] = useState("");
  const [startDate, setStartDate] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (membership) { setRole(membership.role_on_project); setStartDate(membership.start_date); } }, [membership]);
  if (!membership) return null;
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/memberships/${membership.id}`, { role_on_project: role, start_date: startDate });
      toast.success("Updated");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <Dialog open={!!membership} onOpenChange={v => !v && onClose?.()}>
      <DialogContent data-testid="team-edit-dialog">
        <DialogHeader><DialogTitle>Edit {membership.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Role on project</Label><Select value={role} onValueChange={setRole}><SelectTrigger data-testid="team-edit-role"><SelectValue /></SelectTrigger><SelectContent>{(roleOptions || []).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Start date</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="team-edit-start" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="team-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamRemoveDialog({ membership, onClose, projectId, onSaved }) {
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("Resigned");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [openItems, setOpenItems] = useState(null);
  useEffect(() => {
    if (!membership) return;
    setEndDate(new Date().toISOString().slice(0, 10));
    setReason("Resigned"); setNote(""); setOpenItems(null);
    api.get(`/projects/${projectId}/memberships/${membership.id}/open-items`)
      .then(r => setOpenItems(r.data)).catch(() => setOpenItems({ tasks: [], pending_claims: [], unsettled_advances: [] }));
  }, [membership, projectId]);
  if (!membership) return null;
  const save = async () => {
    if (!reason) return toast.error("Pick a reason");
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/memberships/${membership.id}/end`, { end_date: endDate, reason, reason_note: note });
      toast.success("Member removed from project");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <Dialog open={!!membership} onOpenChange={v => !v && onClose?.()}>
      <DialogContent data-testid="team-remove-dialog">
        <DialogHeader><DialogTitle>Remove {membership.name} from this project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {openItems && (openItems.tasks.length + openItems.pending_claims.length + openItems.unsettled_advances.length) > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs space-y-1" data-testid="team-remove-open-items">
              <div className="font-semibold text-amber-900">Still open for this person on this project</div>
              {openItems.tasks.length > 0 && <div>· <b>{openItems.tasks.length}</b> open task(s) — reassign to another active member on the Tasks tab.</div>}
              {openItems.pending_claims.length > 0 && <div>· <b>{openItems.pending_claims.length}</b> pending claim(s) — stay in approval flow and can still be processed.</div>}
              {openItems.unsettled_advances.length > 0 && <div>· <b>{openItems.unsettled_advances.length}</b> unsettled advance(s) — Accounts to settle.</div>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>End date</Label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="team-remove-end" /></div>
            <div><Label>Reason</Label><Select value={reason} onValueChange={setReason}><SelectTrigger data-testid="team-remove-reason"><SelectValue /></SelectTrigger><SelectContent>{["Resigned", "Transferred", "Contract ended", "Other"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Note (optional)</Label><Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} data-testid="team-remove-note" /></div>
          <div className="text-[11px] text-slate-500">Removing from this project does not disable the user's login — that stays in Users &amp; Roles.</div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" onClick={save} disabled={saving} data-testid="team-remove-save">Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
